import {Injectable} from '@angular/core';
import {BehaviorSubject, of, switchMap} from 'rxjs';
import type {CurrentUserResponse, DateFormatCodeDto, UpdateMyPreferencesResponse} from '../../auth/auth.models';
import type {AuthService} from '../../auth/auth.service';

export type RegionalPreferences = {
  dateFormatCode: DateFormatCodeDto | null;
  timeZoneId: string | null;
};

const DEFAULT_PREFERENCES: RegionalPreferences = {
  dateFormatCode: null,
  timeZoneId: null,
};

@Injectable({providedIn: 'root'})
export class RegionalPreferencesService {
  private readonly preferencesSubject = new BehaviorSubject<RegionalPreferences>(DEFAULT_PREFERENCES);
  private initialized = false;

  readonly preferences$ = this.preferencesSubject.asObservable();

  get preferences(): RegionalPreferences {
    return this.preferencesSubject.value;
  }

  initialize(authService: AuthService): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    authService.session$
      .pipe(
        switchMap((session) => session
          ? authService.synchronizeCurrentUserPreferences(session)
          : of(null)),
      )
      .subscribe((currentUser) => this.setFromCurrentUser(currentUser));
  }

  setFromCurrentUser(currentUser: CurrentUserResponse | null): void {
    this.preferencesSubject.next(currentUser
      ? {
          dateFormatCode: currentUser.preferredDateFormatCode,
          timeZoneId: currentUser.preferredTimeZoneId,
        }
      : DEFAULT_PREFERENCES);
  }

  setFromUpdate(response: UpdateMyPreferencesResponse): void {
    this.preferencesSubject.next({
      dateFormatCode: response.preferredDateFormatCode,
      timeZoneId: response.preferredTimeZoneId,
    });
  }
}
